import { format } from 'date-fns'
import { es } from 'date-fns/locale'
import {
  Dialog,
  DialogContent,
  DialogClose,
} from '@/shared/components/ui/dialog'
import { Badge } from '@/shared/components/ui/badge'
import { Skeleton } from '@/shared/components/ui/skeleton'
import { Mail, Phone, MapPin, Building2, Calendar, Clock, X } from 'lucide-react'

function WhatsAppIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className}
    >
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
    </svg>
  )
}
import { useMedifarmaRegistro } from '@/modules/medifarma/hooks/useMedifarma'
import { getEstadoBadgeClass } from '../utils/estados'

interface RegistroDetailSheetProps {
  registroId: number | null
  open: boolean
  onOpenChange: (open: boolean) => void
}


export function RegistroDetailSheet({ registroId, open, onOpenChange }: RegistroDetailSheetProps) {
  const { data: registro, isLoading } = useMedifarmaRegistro(registroId || 0)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg p-0 overflow-hidden" hideCloseButton>
        {isLoading ? (
          <div className="p-6 space-y-6">
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-32 w-full" />
          </div>
        ) : registro ? (
          <>
            {/* Header */}
            <div className="p-6 bg-gray-200 dark:bg-[hsl(220,15%,16%)] text-gray-900 dark:text-gray-100">
              <div className="flex items-center justify-between">
                {/* Izquierda: Nombre */}
                <p className="font-semibold text-lg">{registro.nombre}</p>

                {/* Derecha: Estado y boton cerrar */}
                <div className="flex items-center gap-3">
                  <Badge className={`${getEstadoBadgeClass(registro.estado)} text-sm`}>
                    {registro.estado}
                  </Badge>
                  <DialogClose className="rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2">
                    <X className="h-5 w-5" />
                    <span className="sr-only">Cerrar</span>
                  </DialogClose>
                </div>
              </div>
            </div>

            {/* Contenido */}
            <div className="p-6 pt-4 max-h-[60vh] overflow-y-auto">
              {/* Grid horizontal de informacion */}
              <div className="grid grid-cols-2 gap-3">
                {/* Columna 1: Provincia, Farmacia, Registro */}
                <div className="space-y-3">
                  <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                    <MapPin className="h-4 w-4 text-muted-foreground shrink-0" />
                    <div>
                      <p className="text-xs text-muted-foreground">Provincia</p>
                      <p className="font-medium text-sm">{registro.ciudad}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                    <Building2 className="h-4 w-4 text-muted-foreground shrink-0" />
                    <div>
                      <p className="text-xs text-muted-foreground">Farmacia</p>
                      <p className="font-medium text-sm">{registro.farmacia}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                    <Calendar className="h-4 w-4 text-muted-foreground shrink-0" />
                    <div>
                      <p className="text-xs text-muted-foreground">Registro</p>
                      <p className="font-medium text-sm">
                        {format(new Date(registro.fecha_registro), "dd MMM yyyy, HH:mm", {
                          locale: es,
                        })}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Columna 2: Correo, Telefono, Actualizado */}
                <div className="space-y-3">
                  <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                    <Mail className="h-4 w-4 text-muted-foreground shrink-0" />
                    <div className="min-w-0">
                      <p className="text-xs text-muted-foreground">Correo</p>
                      <p className="font-medium text-sm truncate">{registro.correo}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                    <Phone className="h-4 w-4 text-muted-foreground shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-xs text-muted-foreground">Telefono</p>
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-sm">{registro.telefono}</p>
                        <a
                          href={`https://wa.me/${registro.telefono.replace(/[^0-9]/g, '')}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-green-500 hover:text-green-600 transition-colors"
                          title="Abrir chat en WhatsApp"
                        >
                          <WhatsAppIcon className="h-4 w-4" />
                        </a>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                    <Clock className="h-4 w-4 text-muted-foreground shrink-0" />
                    <div>
                      <p className="text-xs text-muted-foreground">Actualizado</p>
                      <p className="font-medium text-sm">
                        {format(new Date(registro.updated_at), "dd MMM yyyy, HH:mm", {
                          locale: es,
                        })}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="flex items-center justify-center h-40 text-muted-foreground">
            No se encontro el registro
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
